import React, { useState, useCallback, useRef } from 'react';
import styled from 'styled-components';

// Styled Components
const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const LeftPanel = styled.div`
  width: 400px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px;
  overflow-y: auto;
`;

const RightPanel = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ControlGroup = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 5px;
  background: ${props => {
    if (props.$variant === 'danger') return '#ff6b6b';
    if (props.$variant === 'warning') return '#ffd43b';
    if (props.$variant === 'success') return '#51cf66';
    return '#4ecdc4';
  }};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 1000px;
  min-height: 700px;
  position: relative;
  overflow: hidden;
`;

const RecursionTree = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  color: white;
`;

const RecursionNode = styled.div`
  background: ${props => {
    if (props.$isCurrent) return '#51cf66';
    if (props.$isVisited) return '#4ecdc4';
    if (props.$isActive) return '#ffd43b';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  border: 2px solid white;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
  transition: all 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -25px;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 20px;
    background: white;
    display: ${props => props.$hasChildren ? 'block' : 'none'};
  }
`;

const RecursionLevel = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 2px;
    background: white;
    display: ${props => props.$hasChildren ? 'block' : 'none'};
  }
`;

const BacktrackingGrid = styled.div`
  display: grid;
  gap: 2px;
  background: white;
  padding: 10px;
  border-radius: 10px;
`;

const GridCell = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => {
    if (props.$isQueen) return '#ff6b6b';
    if (props.$isAttacked) return '#ffd43b';
    if (props.$isVisited) return '#4ecdc4';
    if (props.$isCurrent) return '#51cf66';
    return '#f8f9fa';
  }};
  border: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  cursor: pointer;
`;

const TowerContainer = styled.div`
  display: flex;
  gap: 100px;
  align-items: flex-end;
  height: 300px;
  position: relative;
`;

const Tower = styled.div`
  width: 20px;
  height: 200px;
  background: #8b4513;
  border-radius: 10px;
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 20px;
    background: #8b4513;
    border-radius: 10px;
  }
`;

const Disk = styled.div`
  width: ${props => props.$size * 30 + 60}px;
  height: 30px;
  background: ${props => {
    const colors = ['#ff6b6b', '#4ecdc4', '#ffd43b', '#51cf66', '#845ef7', '#fd7e14'];
    return colors[props.$index % colors.length];
  }};
  border-radius: 15px;
  margin: 2px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
  transition: all 0.5s ease;
  transform: ${props => props.$isMoving ? 'scale(1.1)' : 'scale(1)'};
`;

const MazeContainer = styled.div`
  display: grid;
  gap: 1px;
  background: #333;
  padding: 10px;
  border-radius: 10px;
`;

const MazeCell = styled.div`
  width: 30px;
  height: 30px;
  background: ${props => {
    if (props.$isWall) return '#333';
    if (props.$isPath) return '#51cf66';
    if (props.$isVisited) return '#4ecdc4';
    if (props.$isCurrent) return '#ffd43b';
    if (props.$isStart) return '#51cf66';
    if (props.$isEnd) return '#ff6b6b';
    return '#f8f9fa';
  }};
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  color: white;
  transition: all 0.3s ease;
`;

const InfoPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
`;

const InfoText = styled.p`
  color: white;
  margin: 5px 0;
  font-size: 0.9rem;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatValue = styled.div`
  color: #4ecdc4;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: white;
  font-size: 0.8rem;
  opacity: 0.8;
`;

const OperationLog = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-height: 150px;
  overflow-y: auto;
`;

const LogTitle = styled.h3`
  color: white;
  margin-bottom: 10px;
  font-size: 1rem;
`;

const LogEntry = styled.div`
  color: white;
  font-size: 0.8rem;
  margin: 3px 0;
  padding: 5px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
`;

const StepControls = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: center;
`;

const AlgorithmSelector = styled.select`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid #ff6b6b;
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  color: #ff6b6b;
  text-align: center;
  font-weight: 600;
`;

function RecursionBacktrackingPage() {
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('factorial');
  const [inputValue, setInputValue] = useState('5');
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [operationLog, setOperationLog] = useState([]);
  const [animationSpeed, setAnimationSpeed] = useState(1000);
  const [recursionTree, setRecursionTree] = useState([]);
  const [nQueensBoard, setNQueensBoard] = useState([]);
  const [towerState, setTowerState] = useState(Array.from({ length: 3 }, () => []));
  const [mazeGrid, setMazeGrid] = useState([]);
  const [sudokuGrid, setSudokuGrid] = useState([]);
  const [error, setError] = useState(null);
  const [moveCount, setMoveCount] = useState(0);
  
  const animationRef = useRef(null);

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const addToLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setOperationLog(prev => [...prev.slice(-9), `${timestamp}: ${message}`]);
  };

  const clearLog = () => {
    setOperationLog([]);
  };

  // Factorial Recursion
  const factorialRecursion = async (n, level = 0) => {
    if (n <= 1) {
      setRecursionTree(prev => [...prev, { n, result: 1, level, isBase: true }]);
      await delay(animationSpeed);
      return 1;
    }
    
    setRecursionTree(prev => [...prev, { n, result: null, level, isRecursive: true }]);
    await delay(animationSpeed);
    
    const result = n * await factorialRecursion(n - 1, level + 1);
    
    setRecursionTree(prev => prev.map(node => 
      node.n === n ? { ...node, result, isComplete: true } : node
    ));
    await delay(animationSpeed);
    
    return result;
  };

  // Fibonacci Recursion
  const fibonacciRecursion = async (n, level = 0) => {
    if (n <= 1) {
      setRecursionTree(prev => [...prev, { n, result: n, level, isBase: true }]);
      await delay(animationSpeed);
      return n;
    }
    
    setRecursionTree(prev => [...prev, { n, result: null, level, isRecursive: true }]);
    await delay(animationSpeed);
    
    const left = await fibonacciRecursion(n - 1, level + 1);
    const right = await fibonacciRecursion(n - 2, level + 1);
    const result = left + right;
    
    setRecursionTree(prev => prev.map(node => 
      node.n === n ? { ...node, result, isComplete: true } : node
    ));
    await delay(animationSpeed);
    
    return result;
  };

  // Tower of Hanoi
  const towerOfHanoi = async (n, source, auxiliary, target) => {
    addToLog(`Tower of Hanoi: Moving ${n} disks from tower ${source + 1} to tower ${target + 1}`);
    
    if (n === 1) {
      // Move disk from source to target
      setTowerState(prev => {
        const newState = prev.map(tower => [...tower]);
        if (newState[source] && newState[source].length > 0) {
          const disk = newState[source].pop();
          if (disk && newState[target]) {
            newState[target].push(disk);
            setMoveCount(prev => prev + 1);
            setCurrentStep(prev => prev + 1);
            addToLog(`Moved disk ${disk.size} from tower ${source + 1} to tower ${target + 1}`);
          }
        }
        return newState;
      });
      await delay(animationSpeed);
      return;
    }
    
    // Move n-1 disks from source to auxiliary using target as temporary
    addToLog(`Moving ${n-1} disks from tower ${source + 1} to tower ${auxiliary + 1} (using tower ${target + 1} as auxiliary)`);
    await towerOfHanoi(n - 1, source, target, auxiliary);
    
    // Move the nth disk from source to target
    setTowerState(prev => {
      const newState = prev.map(tower => [...tower]);
      if (newState[source] && newState[source].length > 0) {
        const disk = newState[source].pop();
        if (disk && newState[target]) {
          newState[target].push(disk);
          setMoveCount(prev => prev + 1);
          setCurrentStep(prev => prev + 1);
          addToLog(`Moved disk ${disk.size} from tower ${source + 1} to tower ${target + 1}`);
        }
      }
      return newState;
    });
    await delay(animationSpeed);
    
    // Move n-1 disks from auxiliary to target using source as temporary
    addToLog(`Moving ${n-1} disks from tower ${auxiliary + 1} to tower ${target + 1} (using tower ${source + 1} as auxiliary)`);
    await towerOfHanoi(n - 1, auxiliary, source, target);
  };

  // N-Queens Backtracking
  const isSafe = (board, row, col) => {
    // Check row
    for (let j = 0; j < col; j++) {
      if (board[row][j] === 1) return false;
    }
    
    // Check upper diagonal
    for (let i = row, j = col; i >= 0 && j >= 0; i--, j--) {
      if (board[i][j] === 1) return false;
    }
    
    // Check lower diagonal
    for (let i = row, j = col; i < board.length && j >= 0; i++, j--) {
      if (board[i][j] === 1) return false;
    }
    
    return true;
  };

  const solveNQueens = async (board, col = 0) => {
    if (col >= board.length) {
      addToLog('N-Queens solution found!');
      return true;
    }
    
    for (let row = 0; row < board.length; row++) {
      if (isSafe(board, row, col)) {
        board[row][col] = 1;
        setNQueensBoard([...board]);
        addToLog(`Placing queen at row ${row + 1}, col ${col + 1}`);
        await delay(animationSpeed);
        
        if (await solveNQueens(board, col + 1)) {
          return true;
        }
        
        board[row][col] = 0;
        setNQueensBoard([...board]);
        addToLog(`Backtracking: removing queen from row ${row + 1}, col ${col + 1}`);
        await delay(animationSpeed);
      }
    }
    
    return false;
  };

  // Maze Solver Backtracking
  const solveMaze = async (maze, startRow, startCol, endRow, endCol) => {
    // Create a copy of the maze to avoid mutating the original
    const mazeCopy = maze.map(row => [...row]);
    
    const solveMazeHelper = async (currentRow, currentCol) => {
      // Check boundaries
      if (currentRow < 0 || currentRow >= mazeCopy.length || 
          currentCol < 0 || currentCol >= mazeCopy[0].length) {
        return false;
      }
      
      // Check if current position is a wall or already visited
      if (mazeCopy[currentRow][currentCol] === 1 || 
          mazeCopy[currentRow][currentCol] === 3) {
        return false;
      }
      
      // Check if we reached the end
      if (currentRow === endRow && currentCol === endCol) {
        mazeCopy[currentRow][currentCol] = 2; // Mark as path
        setMazeGrid([...mazeCopy]);
        setCurrentStep(prev => prev + 1);
        addToLog('Maze solved! Path found to exit');
        return true;
      }
      
      // Mark current position as visited
      mazeCopy[currentRow][currentCol] = 3;
      setMazeGrid([...mazeCopy]);
      setCurrentStep(prev => prev + 1);
      addToLog(`Exploring position (${currentRow + 1}, ${currentCol + 1})`);
      await delay(animationSpeed);
      
      // Try all four directions in order: right, down, left, up
      const directions = [
        [0, 1],   // right
        [1, 0],   // down
        [0, -1],  // left
        [-1, 0]   // up
      ];
      
      for (const [dr, dc] of directions) {
        const newRow = currentRow + dr;
        const newCol = currentCol + dc;
        
        if (await solveMazeHelper(newRow, newCol)) {
          // Mark current position as part of the path
          mazeCopy[currentRow][currentCol] = 2;
          setMazeGrid([...mazeCopy]);
          addToLog(`Path found through (${currentRow + 1}, ${currentCol + 1})`);
          await delay(animationSpeed);
          return true;
        }
      }
      
      // If no path found from this position, mark as backtracked
      mazeCopy[currentRow][currentCol] = 0; // Backtrack
      setMazeGrid([...mazeCopy]);
      addToLog(`Backtracking from (${currentRow + 1}, ${currentCol + 1})`);
      await delay(animationSpeed);
      
      return false;
    };
    
    // Start solving from the start position
    return await solveMazeHelper(startRow, startCol);
  };

  // Sudoku Solver Backtracking
  const isValid = (grid, row, col, num) => {
    // Check row
    for (let x = 0; x < 9; x++) {
      if (grid[row][x] === num) return false;
    }
    
    // Check column
    for (let x = 0; x < 9; x++) {
      if (grid[x][col] === num) return false;
    }
    
    // Check 3x3 box
    const startRow = Math.floor(row / 3) * 3;
    const startCol = Math.floor(col / 3) * 3;
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (grid[i + startRow][j + startCol] === num) return false;
      }
    }
    
    return true;
  };

  const solveSudoku = async (grid) => {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (grid[row][col] === 0) {
          for (let num = 1; num <= 9; num++) {
            if (isValid(grid, row, col, num)) {
              grid[row][col] = num;
              setSudokuGrid([...grid]);
              addToLog(`Trying ${num} at position (${row + 1}, ${col + 1})`);
              await delay(animationSpeed);
              
              if (await solveSudoku(grid)) {
                return true;
              }
              
              grid[row][col] = 0;
              setSudokuGrid([...grid]);
              addToLog(`Backtracking: removing ${num} from (${row + 1}, ${col + 1})`);
              await delay(animationSpeed);
            }
          }
          return false;
        }
      }
    }
    addToLog('Sudoku solved successfully!');
    return true;
  };

  const runAlgorithm = async () => {
    if (!inputValue.trim()) return;
    
    const value = parseInt(inputValue.trim());
    if (isNaN(value) || value <= 0) {
      setError('Please enter a valid positive number');
      addToLog('Please enter a valid positive number');
      return;
    }
    
    setIsRunning(true);
    setCurrentStep(0);
    setError(null);
    setRecursionTree([]);
    clearLog();
    
    try {
      switch (selectedAlgorithm) {
        case 'factorial':
          addToLog(`Starting factorial calculation for ${value}`);
          const result = await factorialRecursion(value);
          addToLog(`Factorial of ${value} = ${result}`);
          break;
          
        case 'fibonacci':
          addToLog(`Starting Fibonacci calculation for ${value}`);
          const fibResult = await fibonacciRecursion(value);
          addToLog(`Fibonacci number at position ${value} = ${fibResult}`);
          break;
          
        case 'towerOfHanoi':
          addToLog(`Starting Tower of Hanoi for ${value} disks`);
          const expectedMoves = Math.pow(2, value) - 1;
          setTotalSteps(expectedMoves);
          setCurrentStep(0);
          setMoveCount(0);
          addToLog(`Expected moves: ${expectedMoves}`);
          const towers = Array.from({ length: 3 }, (_, i) => 
            i === 0 ? Array.from({ length: value }, (_, j) => ({ size: value - j, index: j })) : []
          );
          setTowerState(towers);
          await delay(500);
          if (value > 0) {
            await towerOfHanoi(value, 0, 1, 2);
            addToLog(`Tower of Hanoi completed in ${moveCount} moves!`);
          } else {
            addToLog('Invalid number of disks for Tower of Hanoi');
          }
          break;
          
        case 'nQueens':
          addToLog(`Starting N-Queens solution for ${value}x${value} board`);
          const board = Array(value).fill().map(() => Array(value).fill(0));
          setNQueensBoard(board);
          await delay(500);
          await solveNQueens(board);
          break;
          
        case 'mazeSolver':
          addToLog(`Starting maze solver for ${value}x${value} maze`);
          const expectedSteps = value * value * 2; // Rough estimate for maze solving
          setTotalSteps(expectedSteps);
          setCurrentStep(0);
          addToLog(`Expected steps: ${expectedSteps}`);
          const maze = generateMaze(value);
          setMazeGrid(maze);
          await delay(500);
          await solveMaze(maze, 0, 0, value - 1, value - 1);
          addToLog(`Maze solving completed in ${currentStep} steps`);
          break;
          
        case 'sudokuSolver':
          addToLog('Starting Sudoku solver');
          const sudoku = generateSudoku();
          setSudokuGrid(sudoku);
          await delay(500);
          await solveSudoku(sudoku);
          break;
          
        default:
          setError('Unknown algorithm selected');
          addToLog('Unknown algorithm selected');
      }
    } catch (error) {
      const errorMessage = `Error: ${error.message}`;
      setError(errorMessage);
      addToLog(errorMessage);
      console.error('Algorithm execution error:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const generateMaze = (size) => {
    // Create a maze with guaranteed path from start to end
    const maze = Array(size).fill().map(() => Array(size).fill(0));
    
    // Create a simple path from start to end (diagonal path)
    for (let i = 0; i < size; i++) {
      maze[i][i] = 0; // Ensure diagonal path is clear
    }
    
    // Add some random walls that don't block the path
    for (let i = 0; i < size * size / 4; i++) {
      const row = Math.floor(Math.random() * size);
      const col = Math.floor(Math.random() * size);
      
      // Don't block start, end, or diagonal path
      if (!(row === 0 && col === 0) && 
          !(row === size - 1 && col === size - 1) &&
          !(row === col)) { // Don't block diagonal path
        maze[row][col] = 1;
      }
    }
    
    // Mark start and end
    maze[0][0] = 4; // Start
    maze[size - 1][size - 1] = 5; // End
    
    return maze;
  };

  const generateSudoku = () => {
    const grid = Array(9).fill().map(() => Array(9).fill(0));
    
    // Add some random numbers
    for (let i = 0; i < 20; i++) {
      const row = Math.floor(Math.random() * 9);
      const col = Math.floor(Math.random() * 9);
      const num = Math.floor(Math.random() * 9) + 1;
      if (isValid(grid, row, col, num)) {
        grid[row][col] = num;
      }
    }
    
    return grid;
  };

  const resetVisualization = () => {
    setRecursionTree([]);
    setNQueensBoard([]);
    setTowerState(Array.from({ length: 3 }, () => []));
    setMazeGrid([]);
    setSudokuGrid([]);
    setCurrentStep(0);
    setTotalSteps(0);
    setMoveCount(0);
    setError(null);
    clearLog();
  };

  const handleAlgorithmChange = (e) => {
    setSelectedAlgorithm(e.target.value);
    setError(null);
    resetVisualization();
  };

  const renderRecursionTree = () => {
    if (recursionTree.length === 0) return null;
    
    const maxLevel = Math.max(...recursionTree.map(node => node.level));
    const levels = Array(maxLevel + 1).fill().map(() => []);
    
    recursionTree.forEach(node => {
      levels[node.level].push(node);
    });
    
    return (
      <RecursionTree>
        {levels.map((levelNodes, levelIndex) => (
          <RecursionLevel key={levelIndex} $hasChildren={levelIndex < maxLevel}>
            {levelNodes.map((node, nodeIndex) => (
              <RecursionNode
                key={`${levelIndex}-${nodeIndex}`}
                $isCurrent={node.isRecursive && !node.isComplete}
                $isVisited={node.isComplete}
                $isActive={node.isBase}
                $hasChildren={levelIndex < maxLevel}
              >
                {node.isComplete ? `${node.n}! = ${node.result}` : `${node.n}!`}
              </RecursionNode>
            ))}
          </RecursionLevel>
        ))}
      </RecursionTree>
    );
  };

  const renderNQueensBoard = () => {
    try {
      if (!nQueensBoard || nQueensBoard.length === 0) return null;
      
      return (
        <BacktrackingGrid style={{ gridTemplateColumns: `repeat(${nQueensBoard.length}, 40px)` }}>
          {nQueensBoard.flat().map((cell, index) => {
            const row = Math.floor(index / nQueensBoard.length);
            const col = index % nQueensBoard.length;
            return (
              <GridCell
                key={index}
                $isQueen={cell === 1}
                $isAttacked={false}
                $isVisited={false}
                $isCurrent={false}
              >
                {cell === 1 ? '👑' : ''}
              </GridCell>
            );
          })}
        </BacktrackingGrid>
      );
    } catch (error) {
      console.error('Error rendering N-Queens board:', error);
      return <div style={{ color: 'white', textAlign: 'center' }}>Error rendering N-Queens board</div>;
    }
  };

  const renderTowerOfHanoi = () => {
    try {
      if (!towerState || towerState.length === 0) return null;
      
      return (
        <div style={{ textAlign: 'center', color: 'white' }}>
          <h3 style={{ marginBottom: '20px' }}>Tower of Hanoi</h3>
          <TowerContainer>
            {towerState.map((tower, towerIndex) => (
              <div key={towerIndex} style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '10px', fontSize: '1.2rem', fontWeight: 'bold' }}>
                  Tower {towerIndex + 1}
                </div>
                <Tower>
                  {tower && Array.isArray(tower) && tower.length > 0 ? tower.map((disk, diskIndex) => (
                    <Disk
                      key={diskIndex}
                      $size={disk && disk.size ? disk.size : 1}
                      $index={disk && disk.index ? disk.index : 0}
                      $isMoving={false}
                    >
                      {disk && disk.size ? disk.size : 1}
                    </Disk>
                  )) : null}
                </Tower>
                <div style={{ marginTop: '10px', fontSize: '0.9rem', opacity: 0.8 }}>
                  {tower && Array.isArray(tower) ? tower.length : 0} disks
                </div>
              </div>
            ))}
          </TowerContainer>
          <div style={{ marginTop: '20px', fontSize: '0.9rem', opacity: 0.7 }}>
            Goal: Move all disks from Tower 1 to Tower 3
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error rendering Tower of Hanoi:', error);
      return <div style={{ color: 'white', textAlign: 'center' }}>Error rendering Tower of Hanoi</div>;
    }
  };

  const renderMaze = () => {
    if (mazeGrid.length === 0) return null;
    
    return (
      <div style={{ textAlign: 'center', color: 'white' }}>
        <h3 style={{ marginBottom: '20px' }}>Maze Solver</h3>
        <MazeContainer style={{ gridTemplateColumns: `repeat(${mazeGrid[0].length}, 30px)` }}>
          {mazeGrid.flat().map((cell, index) => {
            const row = Math.floor(index / mazeGrid[0].length);
            const col = index % mazeGrid[0].length;
            return (
              <MazeCell
                key={index}
                $isWall={cell === 1}
                $isPath={cell === 2}
                $isVisited={cell === 3}
                $isCurrent={false}
                $isStart={cell === 4}
                $isEnd={cell === 5}
              >
                {cell === 4 ? '🚀' : cell === 5 ? '🏁' : ''}
              </MazeCell>
            );
          })}
        </MazeContainer>
        
        {/* Maze Legend */}
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '20px', height: '20px', backgroundColor: '#51cf66', borderRadius: '3px' }}></div>
            <span>Start (🚀)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '20px', height: '20px', backgroundColor: '#ff6b6b', borderRadius: '3px' }}></div>
            <span>End (🏁)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '20px', height: '20px', backgroundColor: '#4ecdc4', borderRadius: '3px' }}></div>
            <span>Visited</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '20px', height: '20px', backgroundColor: '#51cf66', borderRadius: '3px' }}></div>
            <span>Path</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '20px', height: '20px', backgroundColor: '#333', borderRadius: '3px' }}></div>
            <span>Wall</span>
          </div>
        </div>
        
        <div style={{ marginTop: '15px', fontSize: '0.9rem', opacity: 0.7 }}>
          Goal: Find path from Start (🚀) to End (🏁) using backtracking
        </div>
      </div>
    );
  };

  const renderSudoku = () => {
    if (sudokuGrid.length === 0) return null;
    
    return (
      <BacktrackingGrid style={{ gridTemplateColumns: 'repeat(9, 40px)' }}>
        {sudokuGrid.flat().map((cell, index) => {
          const row = Math.floor(index / 9);
          const col = index % 9;
          return (
            <GridCell
              key={index}
              $isQueen={false}
              $isAttacked={false}
              $isVisited={false}
              $isCurrent={false}
            >
              {cell !== 0 ? cell : ''}
            </GridCell>
          );
        })}
      </BacktrackingGrid>
    );
  };

  const renderVisualization = () => {
    if (!selectedAlgorithm) {
      return <div style={{ color: 'white', textAlign: 'center' }}>Select an algorithm to start</div>;
    }
    
    switch (selectedAlgorithm) {
      case 'factorial':
      case 'fibonacci':
        return renderRecursionTree();
      case 'nQueens':
        return renderNQueensBoard();
      case 'towerOfHanoi':
        return renderTowerOfHanoi();
      case 'mazeSolver':
        return renderMaze();
      case 'sudokuSolver':
        return renderSudoku();
      default:
        return <div style={{ color: 'white', textAlign: 'center' }}>Select an algorithm to start</div>;
    }
  };

  return (
    <PageContainer>
      <LeftPanel>
        <Title>Recursion & Backtracking</Title>
        
        <Controls>
          {/* Algorithm Selection */}
          <ControlGroup>
            <Label>Select Algorithm</Label>
            <AlgorithmSelector
              value={selectedAlgorithm}
              onChange={handleAlgorithmChange}
            >
              <option value="factorial">Factorial Recursion</option>
              <option value="fibonacci">Fibonacci Recursion</option>
              <option value="towerOfHanoi">Tower of Hanoi</option>
              <option value="nQueens">N-Queens Backtracking</option>
              <option value="mazeSolver">Maze Solver Backtracking</option>
              <option value="sudokuSolver">Sudoku Solver Backtracking</option>
            </AlgorithmSelector>
          </ControlGroup>
          
          {/* Input Control */}
          <ControlGroup>
            <Label>Input Value</Label>
            <Input
              type="text"
              placeholder="Enter value (e.g., 5)"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && runAlgorithm()}
            />
            <Button onClick={runAlgorithm} disabled={isRunning} $variant="success">
              {isRunning ? 'Running...' : 'Run Algorithm'}
            </Button>
            <Button onClick={resetVisualization} disabled={isRunning} $variant="warning">
              Reset
            </Button>
          </ControlGroup>
          
          {/* Animation Speed Control */}
          <ControlGroup>
            <Label>Animation Speed: {animationSpeed}ms</Label>
            <Input
              type="range"
              min="500"
              max="3000"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseInt(e.target.value))}
            />
          </ControlGroup>
          
          {/* Stats */}
          <StatsContainer>
            <StatBox>
              <StatValue>{currentStep}</StatValue>
              <StatLabel>Current Step</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{totalSteps}</StatValue>
              <StatLabel>Total Steps</StatLabel>
            </StatBox>
            {selectedAlgorithm === 'towerOfHanoi' && (
              <StatBox>
                <StatValue>{moveCount}</StatValue>
                <StatLabel>Moves Made</StatLabel>
              </StatBox>
            )}
          </StatsContainer>
          
          {/* Operation Log */}
          <OperationLog>
            <LogTitle>Operation Log</LogTitle>
            {operationLog.map((entry, index) => (
              <LogEntry key={index}>{entry}</LogEntry>
            ))}
          </OperationLog>
          
          {/* Error Display */}
          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}
        </Controls>
      </LeftPanel>
      
      <RightPanel>
        <VisualizationContainer>
          {renderVisualization()}
          
          {/* Algorithm Info */}
          <InfoPanel>
            <InfoText>
              <strong>{selectedAlgorithm === 'factorial' && 'Factorial Recursion:'}</strong>
              {selectedAlgorithm === 'factorial' && ' Demonstrates simple recursion with n! = n × (n-1)!'}
            </InfoText>
            <InfoText>
              <strong>{selectedAlgorithm === 'fibonacci' && 'Fibonacci Recursion:'}</strong>
              {selectedAlgorithm === 'fibonacci' && ' Shows multiple recursive calls with F(n) = F(n-1) + F(n-2)'}
            </InfoText>
            <InfoText>
              <strong>{selectedAlgorithm === 'towerOfHanoi' && 'Tower of Hanoi:'}</strong>
              {selectedAlgorithm === 'towerOfHanoi' && ' Classic recursion problem moving disks between towers'}
            </InfoText>
            <InfoText>
              <strong>{selectedAlgorithm === 'nQueens' && 'N-Queens Backtracking:'}</strong>
              {selectedAlgorithm === 'nQueens' && ' Place N queens on N×N board without attacking each other'}
            </InfoText>
            <InfoText>
              <strong>{selectedAlgorithm === 'mazeSolver' && 'Maze Solver:'}</strong>
              {selectedAlgorithm === 'mazeSolver' && ' Find path through maze using backtracking algorithm'}
            </InfoText>
            <InfoText>
              <strong>{selectedAlgorithm === 'sudokuSolver' && 'Sudoku Solver:'}</strong>
              {selectedAlgorithm === 'sudokuSolver' && ' Solve Sudoku puzzle using backtracking technique'}
            </InfoText>
          </InfoPanel>
        </VisualizationContainer>
      </RightPanel>
    </PageContainer>
  );
}

export default RecursionBacktrackingPage;
